import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    MoveInspirationItemsMutation,
    MoveInspirationItemsMutationVariables,
} from 'generated/graphql';
import { MoveInspirationItemsDocument } from 'generated/graphql';

export const MOVE_BUILD_HUB_INSPIRATION_ITEMS_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-items-move';

const DEFAULT_FAILED_MESSAGE = 'Failed to move BuildHub inspiration items';

/**
 * Action to move items from one mood board to another within a BuildHub project.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(
        data.get('destinationBoardId'),
        'Destination moodboard id required for moving to.'
    );
    invariant(
        data.get('boardId'),
        'Source moodboard id required for moving from.'
    );
    invariant(data.get('pinListString'), 'pin ids required for move.');
    invariant(data.get('buildHubUrl'), 'Moodboard BuildHub url required.');

    const pins = data.get('pinListString') as string;
    const variables: MoveInspirationItemsMutationVariables = {
        FromBoardID: Number(data.get('boardId')),
        ToBoardID: Number(data.get('destinationBoardId')),
        BuildHubUrl: data.get('buildHubUrl') as string,
        Pins: pins.split(','),
    };

    try {
        const { data: result } = await context.client.mutate<
            MoveInspirationItemsMutation,
            MoveInspirationItemsMutationVariables
        >({
            mutation: MoveInspirationItemsDocument,
            variables,
        });

        const validResult = result?.moodBoardPinsMove;
        if (!validResult) {
            return json({
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
